import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store/index";
import { ModalGameGeoTabs } from "configs/types";
import instance from "api/apiClient";

import SearchInput from "components/inputs/SearchInput/SearchInput";
import { Checkbox } from "components/table/components/Checkbox/Checkbox";
import styles from "./GamesGeoTab.module.scss";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import cn from "clsx";

type Props = {
  canBeUpdated?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
};

const GamesGeoTab: React.FC<Props> = ({ canBeUpdated, onLoadingChange }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const geoList = useAppSelector(selectFilteredGeo);

  const [tabs] = useState<ModalGameGeoTabs[]>([
    ModalGameGeoTabs.collections,
    ModalGameGeoTabs.tags,
    ModalGameGeoTabs.tournaments,
    ModalGameGeoTabs.jackpots,
  ]);
  const [activeTab, setActiveTab] = useState<ModalGameGeoTabs>(
    ModalGameGeoTabs.collections,
  );
  const [isFetched, setFetched] = useState<boolean>(false);
  const [isFetching, setFetching] = useState<boolean>(false);
  const [itemsFull, setItemsFull] = useState<
    {
      id: number;
      title: string;
      image: string;
      geo: { geo: string; accepted: boolean }[];
    }[]
  >([]);
  const [items, setItems] = useState<
    {
      id: number;
      title: string;
      image: string;
      geo: { geo: string; accepted: boolean }[];
    }[]
  >([]);
  const [searchValue, setSearchValue] = useState<string>("");

  const dispatch = useAppDispatch();

  useEffect(() => {
    onLoadingChange?.(!isFetched);
  }, [isFetched, onLoadingChange]);

  const handleLoadItem = async () => {
    const requiresGeoData =
      activeTab === ModalGameGeoTabs.tags ||
      activeTab === ModalGameGeoTabs.collections;

    if (!requiresGeoData) {
      setItemsFull([]);
      setFetched(true);
      setFetching(false);
      return;
    }

    try {
      if (activeTab === ModalGameGeoTabs.tags) {
        const result = await instance.get(
          `game/${searchParams.get("entityId") ?? 1}/tags-by-geo`,
        );
        setItemsFull(result.data.items);
      }
      if (activeTab === ModalGameGeoTabs.collections) {
        const result = await instance.get(
          `game/${searchParams.get("entityId") ?? 1}/collections-by-geo`,
        );
        setItemsFull(result.data.items);
      }
    } catch {
      console.log("handleLoadItem");
    }
    setFetched(true);
    setFetching(false);
  };

  const handleItemClick = async (
    id: number,
    geo: string,
    isActive: boolean,
  ) => {
    setFetching(true);
    try {
      if (activeTab === ModalGameGeoTabs.tags) {
        if (isActive) {
          await instance.put(`game/tag/${id}/remove-games`, {
            geos: [geo],
            gameIds: [`${searchParams.get("entityId") ?? 1}`],
          });
        } else {
          await instance.put(`game/tag/${id}/add-games`, {
            geos: [geo],
            gameIds: [`${searchParams.get("entityId") ?? 1}`],
          });
        }
      }
      if (activeTab === ModalGameGeoTabs.collections) {
        if (isActive) {
          await instance.put(`game/collection/${id}/remove-games`, {
            geos: [geo],
            gameIds: [`${searchParams.get("entityId") ?? 1}`],
          });
        } else {
          await instance.put(`game/collection/${id}/add-games`, {
            geos: [geo],
            gameIds: [`${searchParams.get("entityId") ?? 1}`],
          });
        }
      }
      dispatch(
        showNotification({
          title: t("success"),
          message: t("gamesHasBeenUpdatedSuccessfully"),
          type: NotificationTypes.success,
        }),
      );
    } catch (error) {
      console.log(error);
      dispatch(
        showNotification({
          title: t("error"),
          message: t("updateFailed"),
          type: NotificationTypes.error,
        }),
      );
    }
    handleLoadItem();
  };

  useEffect(() => {
    setSearchValue("");
    if (
      activeTab !== ModalGameGeoTabs.tags &&
      activeTab !== ModalGameGeoTabs.collections
    ) {
      handleLoadItem();
      return;
    }

    setFetched(false);
    handleLoadItem();
  }, [activeTab]);

  const handleTabClick = (e: ModalGameGeoTabs) => {
    onLoadingChange?.(
      e === ModalGameGeoTabs.tags || e === ModalGameGeoTabs.collections,
    );
    setActiveTab(e);
  };

  const isActive = (
    geo: { geo: string; accepted: boolean }[],
    item: string,
  ) => {
    const result = geo.filter((el) => el.geo === item && el.accepted);
    if (result.length) {
      return true;
    } else {
      return false;
    }
  };

  const onSearch = (e: string) => {
    setSearchValue(e);
  };

  useEffect(() => {
    setItems(
      itemsFull.filter((el) =>
        el.title?.toLowerCase().includes(searchValue?.toLowerCase()),
      ),
    );
  }, [searchValue, itemsFull]);

  const renderTab = () => {
    if (
      activeTab === ModalGameGeoTabs.tags ||
      activeTab === ModalGameGeoTabs.collections
    ) {
      return (
        <div className="game-geos-list">
          <SearchInput
            onChange={onSearch}
            value={searchValue}
            placeholder={t("searchYourEntity")}
          />
          <div className="game-geos-list-items">
            {items.map((el) => (
              <div className="game-geos-list-item" key={el.title}>
                <div className="title">
                  <img src={el.image} alt="" />
                  <span>{el.title}</span>
                </div>
                <div className="geos">
                  {geoList.map((item) => (
                    <div
                      onClick={() => {
                        if (!canBeUpdated) return;
                        handleItemClick(el.id, item, isActive(el.geo, item));
                      }}
                      className={cn(
                        "item",
                        isActive(el.geo, item) && "active",
                        isFetching || (!canBeUpdated && "disabled"),
                      )}
                      key={item}
                    >
                      <span>{item}</span>
                      <Checkbox
                        id={item}
                        checked={isActive(el.geo, item)}
                        disabled={isFetching || !canBeUpdated}
                        onClick={() => {
                          if (!canBeUpdated || isFetching) return;
                          handleItemClick(el.id, item, isActive(el.geo, item));
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return <p className={styles.text}>No data found</p>;
  };

  return (
    <div className="game-geos-wrapper">
      {isFetched ? (
        <>
          <div className="game-modal-tabs small">
            {tabs.map((el) => (
              <div
                className={`tab-item ${activeTab === el ? "active" : ""}`}
                key={el}
                onClick={() => {
                  handleTabClick(el);
                }}
              >
                {t(el)}
              </div>
            ))}
          </div>
          {renderTab()}
        </>
      ) : (
        <ModalTabSkeleton />
      )}
    </div>
  );
};

export default GamesGeoTab;
